import { useState, useContext } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useGlobalInventory = () => {
  const { api, bumppyApi, userID } = useContext(GlobalContext);

  const [masterCategories, setMasterCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [allInventoryCategories, setAllInventoryCategories] = useState([]);
  const [loadingAllCategories, setLoadingAllCategories] = useState(false);

  const ALL_CATEGORIES_CACHE_KEY = `global_inventory_all_categories_${userID}`;

  const uploadGlobalInventoryImage = async ({
    imageData,
    itemName = "inventory_image",
  }) => {
    if (!imageData) return "";

    if (!String(imageData).startsWith("data:")) {
      return imageData;
    }

    if (!bumppyApi) {
      throw new Error("Bumppy API instance missing");
    }

    if (!userID) {
      throw new Error("User ID missing");
    }

    try {
      const response = await bumppyApi.post("/v1/uploadItemImagev2.php", {
        userid: String(userID),
        item_name: String(itemName || "inventory_image"),
        type: "IMG",
        image_data: imageData,
      });

      if (String(response?.data?.status) === "0" && response?.data?.image_url) {
        return response.data.image_url;
      }

      throw new Error(
        response?.data?.message ||
          response?.data?.responseMessage ||
          "Image upload failed",
      );
    } catch (error) {
      console.error(
        "Global inventory image upload error:",
        error?.response?.data || error,
      );

      throw new Error(
        error?.response?.data?.message ||
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Image upload failed",
      );
    }
  };

  // MASTER CATEGORIES
  const fetchMasterCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await api.post(
        "/Inventory/v1.0/getMasterInventoryCategoriesV2",
        {
          userid: userID,
          recordPerPage: "500",
          pageIndex: "1",
        },
      );

      const data = res?.data?.response || [];
      setMasterCategories(data);
      return data;
    } catch (err) {
      console.error("fetchMasterCategories error", err);
      return [];
    } finally {
      setLoadingCategories(false);
    }
  };

  // GET ALL INVENTORY CATEGORIES WITH SUBCATEGORIES AND ITEMS
  const fetchInventoryAllCategories = async (forceRefresh = false) => {
    setLoadingAllCategories(true);

    try {
      if (!forceRefresh) {
        const cached = sessionStorage.getItem(ALL_CATEGORIES_CACHE_KEY);

        if (cached) {
          const parsedData = JSON.parse(cached);

          if (Array.isArray(parsedData)) {
            setAllInventoryCategories(parsedData);
            return parsedData;
          }
        }
      }

      const res = await api.post("/Inventory/getInventoryAllCategories", {
        userid: userID,
      });

      if (res?.data?.responseCode === "00") {
        const data = Array.isArray(res?.data?.response)
          ? res.data.response
          : [];

        setAllInventoryCategories(data);
        sessionStorage.setItem(ALL_CATEGORIES_CACHE_KEY, JSON.stringify(data));

        return data;
      }

      setAllInventoryCategories([]);
      return [];
    } catch (err) {
      console.error("fetchInventoryAllCategories error", err);
      setAllInventoryCategories([]);
      return [];
    } finally {
      setLoadingAllCategories(false);
    }
  };

  // PRODUCTS
  const fetchProductsByCategory = async (categoryId) => {
    setLoadingItems(true);

    try {
      const res = await api.post("/Inventory/v1.0/getMasterInventoryItems", {
        userid: userID,
        categoryId: categoryId.toString(),
      });

      const data = res?.data?.response || [];

      const flatProducts = [];

      data.forEach((cat) => {
        cat.subCategories?.forEach((sub) => {
          sub.products?.forEach((prod) => {
            flatProducts.push({
              ...prod,
              category: cat.categoryName,
              subCategory: sub.subCategoryName,
            });
          });
        });
      });

      setInventoryItems(flatProducts);
      return flatProducts;
    } catch (err) {
      console.error("fetchProductsByCategory error", err);
      return [];
    } finally {
      setLoadingItems(false);
    }
  };

  // SEARCH
  const searchInventory = async (query) => {
    setSearchLoading(true);

    try {
      const res = await api.post("/Inventory/v1.0/getSearchInventoryItem", {
        userid: userID,
        searchWord: query,
      });

      if (res?.data?.responseCode === "00") {
        const list = res?.data?.response || [];

        const normalized = list.map((r, i) => ({
          id: r.id || r.itemId || `sr-${i}`,
          productImage: r.productImage || "",
          productName: r.productName || r.name || "Item",
          category: r.category || "",
          subCategory: r.subCategory || "",
          price: r.price || "0",
          discount: r.discount || "0",
        }));

        setSearchResults(normalized);
        return normalized;
      }

      return [];
    } catch (err) {
      console.error("searchInventory error", err);
      return [];
    } finally {
      setSearchLoading(false);
    }
  };

  // ADD TO INVENTORY
  const addItemsToInventory = async (items) => {
    try {
      const res = await api.post("/Inventory/v1.0/addProductToMainInventory", {
        userid: userID,
        items,
      });

      return res.data;
    } catch (err) {
      console.error("addItemsToInventory error", err);
      return null;
    }
  };

  // ADD SUBCATEGORY
  const addInventorySubCategory = async (payload) => {
    try {
     const res = await api.post(
  "/Inventory/v1.0/addInventorySubCategory",
  {
    userid: userID,
    ...payload,
  },
);

      return res.data;
    } catch (err) {
      console.error("addInventorySubCategory error", err);
      return null;
    }
  };

  // ADD INVENTORY CATEGORY
  const addInventoryCategory = async ({ category, subCategoryImage }) => {
    try {
      let imageUrl = subCategoryImage || "";

      if (imageUrl.startsWith("data:")) {
        imageUrl = await uploadGlobalInventoryImage({
          imageData: imageUrl,
          itemName: category || "inventory_category",
        });
      }

      const res = await api.post("/Inventory/v1.0/addInventoryCategory", {
        userid: userID,
        category,
        subCategoryImage: imageUrl,
      });

      return res.data;
    } catch (err) {
      console.error("addInventoryCategory error:", err?.response?.data || err);

      return null;
    }
  };

  // GET ALL INVENTORY VARIANTS
  const fetchAllInventoryVariants = async () => {
    setVariantsLoading(true);
    try {
      const res = await api.post("/Inventory/v1.0/getAllInventoryVariant", {
        userid: userID,
      });

      if (res?.data?.responseCode === "00") {
        const data = res?.data?.response || [];
        setVariants(data);
        return data;
      }
      return [];
    } catch (err) {
      console.error("fetchAllInventoryVariants error", err);
      return [];
    } finally {
      setVariantsLoading(false);
    }
  };

  const updateInventoryVariant = async (payload) => {
    try {
      const res = await api.post(
        "/Inventory/v1.0/updateInventoryVariant",
        payload,
      );

      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // INSERT INVENTORY VARIANT
  const insertInventoryVariant = async (payload) => {
    try {
      const res = await api.post("/Inventory/v1.0/insertInventoryVariant", {
        ...payload,
      });

      return res.data;
    } catch (err) {
      console.error("insertInventoryVariant error", err);
      return null;
    }
  };

  // DELETE INVENTORY VARIANT
  const deleteInventoryVariant = async (variantId) => {
    try {
      const res = await api.post("/Inventory/v1.0/deleteInventoryVariant", {
        variantId: variantId,
      });

      return res.data;
    } catch (err) {
      console.error("deleteInventoryVariant error", err);
      return null;
    }
  };

  // INSERT GLOBAL INVENTORY ITEMS
const insertGlobalInventoryItems = async ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    console.error("insertGlobalInventoryItems: items missing");
    return null;
  }

  try {
    const item = items[0];

    if (!item || typeof item !== "object") {
      console.error("insertGlobalInventoryItems: invalid item data");
      return null;
    }

    let imageUrl = item.imageUrl || "";

    if (imageUrl.startsWith("data:")) {
      imageUrl = await uploadGlobalInventoryImage({
        imageData: imageUrl,
        itemName: item.productName || item.itemName || "global_inventory_item",
      });
    }

    const payload = {
      items: [
        {
          ...item,
          imageUrl,
        },
      ],
    };

    const res = await api.post(
      "/Inventory/v1.0/insertGlobalInventoryItems",
      payload,
    );

    if (res?.data?.responseCode === "00") {
      await fetchAllInventoryVariants();
    }

    return res.data;
  } catch (err) {
    console.error(
      "insertGlobalInventoryItems error:",
      err?.response?.data || err,
    );

    return null;
  }
};

  return {
    masterCategories,
    subCategories,
    inventoryItems,
    searchResults,
    loadingCategories,
    loadingItems,
    searchLoading,
    variants,
    variantsLoading,
    allInventoryCategories,
    loadingAllCategories,
    fetchMasterCategories,
    fetchProductsByCategory,
    searchInventory,
    addItemsToInventory,
    addInventorySubCategory,
    setSubCategories,
    addInventoryCategory,
    fetchAllInventoryVariants,
    updateInventoryVariant,
    insertInventoryVariant,
    deleteInventoryVariant,
    insertGlobalInventoryItems,
    fetchInventoryAllCategories,
  };
};

export default useGlobalInventory;

